/*
  Desplazamiento suave para el enlace "¿Cómo funcionan las suscripciones?"
  del componente snippets/subscription-benefits.liquid.

  Por qué en JS y no en CSS:
  - assets/base.css declara `scroll-behavior: smooth` sobre `body`, pero el
    viewport toma esa propiedad del elemento raiz (`html`), asi que esa regla no
    tiene efecto y el salto del ancla es instantaneo.
  - Aun aplicandola en `html`, la duracion del scroll nativo la decide el
    navegador y no se puede ajustar. Aqui si es configurable.

  Ajustes por instancia, como data-attributes en la raiz del componente:
    data-scroll-duration  ms de referencia de la animacion (por defecto 900)
    data-scroll-offset    px que se restan al destino, para que la seccion no
                          quede pegada al borde superior (por defecto 80). Subirlo
                          si alguna vez hay un header fijo que tape el destino.

  Solo actua sobre enlaces internos del propio componente; el resto de anclas
  del tema mantienen su comportamiento.
*/
(function () {
  'use strict';

  var LINK_SELECTOR = '.sv-subs-benefits__link, [data-sv-smooth-scroll]';
  var ROOT_SELECTOR = '.sv-subs-benefits';
  var DEFAULT_DURATION = 900;
  // Se para un poco antes del destino, para dejar aire sobre la seccion.
  var DEFAULT_OFFSET = 20;
  // Umbral para no abortar la animacion con el arrastre residual del trackpad.
  var CANCEL_WHEEL_DELTA = 4;
  var CANCEL_KEYS = [' ', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'];
  var activeCancel = null;
  // Distancia de referencia: a esa distancia la animacion dura exactamente
  // data-scroll-duration. Por encima y por debajo se ajusta (ver duracionFor).
  var REFERENCE_DISTANCE = 1500;
  var MIN_FACTOR = 0.6;
  var MAX_FACTOR = 2;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // easeInOutSine: su velocidad maxima es 1.57x la media, frente a 2.97x de
  // easeInOutCubic. Sobre distancias largas (aqui ~2400px) esa diferencia es la
  // que se percibe como "tiron" a mitad de recorrido, asi que el movimiento
  // resulta bastante mas suave aun durando lo mismo.
  function ease(t) {
    return 0.5 * (1 - Math.cos(Math.PI * t));
  }

  // La duracion crece con la distancia en vez de ser fija: recorrer 20.000px en
  // el mismo tiempo que 500px es lo que se percibe como un salto brusco. Crece
  // con la raiz cuadrada (no lineal, o un destino lejano tardaria una eternidad)
  // y queda acotada entre 0.6x y 2x la duracion configurada.
  function durationFor(distance, base) {
    var factor = Math.sqrt(Math.abs(distance) / REFERENCE_DISTANCE);
    factor = Math.max(MIN_FACTOR, Math.min(MAX_FACTOR, factor));
    return base * factor;
  }

  // Salto inmediato explicito: si algun dia `scroll-behavior: smooth` acaba
  // aplicandose al elemento raiz, un scrollTo por frame se animaria por su
  // cuenta y competiria con esta animacion.
  function jumpTo(y) {
    try {
      window.scrollTo({ top: y, left: 0, behavior: 'instant' });
    } catch (e) {
      window.scrollTo(0, y);
    }
  }

  function intSetting(root, name, fallback) {
    var raw = root && root.getAttribute(name);
    var value = parseInt(raw, 10);
    return isNaN(value) ? fallback : value;
  }

  function scrollToTarget(target, duration, offset) {
    if (activeCancel) activeCancel();

    var startY = window.pageYOffset;

    // Se recalcula en cada frame: en paginas largas con carga diferida el
    // contenido de arriba se resuelve mientras bajamos y desplaza el destino,
    // asi que una posicion fijada al inicio deja el scroll corto o pasado.
    function currentEndY() {
      var maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      return Math.min(maxY, Math.max(0, window.pageYOffset + target.getBoundingClientRect().top - offset));
    }

    var distance = currentEndY() - startY;

    if (distance === 0) return;

    var effectiveDuration = durationFor(distance, duration);
    var startTime = null;
    var cancelled = false;

    function stop() {
      if (cancelled) return;
      cancelled = true;
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', onKey);
      if (activeCancel === stop) activeCancel = null;
    }

    // Solo abortamos ante un gesto de scroll real de la persona, no ante el
    // ruido de un trackpad ni ante teclas que no mueven la pagina.
    function onWheel(e) {
      if (Math.abs(e.deltaY) >= CANCEL_WHEEL_DELTA || Math.abs(e.deltaX) >= CANCEL_WHEEL_DELTA) stop();
    }
    function onKey(e) {
      if (CANCEL_KEYS.indexOf(e.key) !== -1) stop();
    }

    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', onKey);
    activeCancel = stop;

    function step(now) {
      if (cancelled) return;
      if (startTime === null) startTime = now;

      var progress = Math.min(1, (now - startTime) / effectiveDuration);
      jumpTo(startY + (currentEndY() - startY) * ease(progress));

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        stop();
      }
    }

    window.requestAnimationFrame(step);
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest ? event.target.closest(LINK_SELECTOR) : null;
    if (!link) return;

    var href = link.getAttribute('href') || '';
    // '#' a secas es el caso "abrir sidebar" (onclick), no un destino.
    if (href.charAt(0) !== '#' || href.length < 2) return;

    var target;
    try {
      target = document.querySelector(href);
    } catch (e) {
      return; // href no es un selector valido; que lo maneje el navegador
    }
    if (!target) return;

    event.preventDefault();

    var root = link.closest(ROOT_SELECTOR);
    var offset = intSetting(root, 'data-scroll-offset', DEFAULT_OFFSET);

    if (prefersReducedMotion()) {
      jumpTo(Math.max(0, window.pageYOffset + target.getBoundingClientRect().top - offset));
    } else {
      scrollToTarget(target, intSetting(root, 'data-scroll-duration', DEFAULT_DURATION), offset);
    }

    // Reflejamos el destino en la URL sin provocar un segundo salto.
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', href);
    }
  });
})();
